'use client';

import { RingFinishEnum, TypographyV3 } from '@jouzen/ecom-components';
import { useDynamicConfig } from '@statsig/react-bindings';

import useDiscountMessage from '@/app/[locale]/product/_hooks/useDiscountMessage';
import usePDPExperiment from '@/app/[locale]/product/rings/oura-ring-4/(wizard)/[finish]/_hooks/usePDPExperiment';
import { POP_PATHNAME } from '@/app/consts/pathnames';
import { RingStyleEnum } from '@/app/enums/RingStyleEnum';
import usePathnameWithoutLocale from '@/app/hooks/usePathnameWithoutLocale';

import type { GenericEyebrowProps } from '../EyebrowMessage';
import { AffiliateMessage, GenericEyebrow } from '../EyebrowMessage';
import type { EyebrowConfig, EyebrowProps } from '../types';

const HARDWARE_PDP_ROUTES = [
  `${POP_PATHNAME}/${RingStyleEnum.OuraRing4}/${RingFinishEnum.BrushedSilver}`,
  `${POP_PATHNAME}/${RingStyleEnum.OuraRing4}/${RingFinishEnum.Black}`,
  `${POP_PATHNAME}/${RingStyleEnum.OuraRing4}/${RingFinishEnum.Gold}`,
  `${POP_PATHNAME}/${RingStyleEnum.OuraRing4}/${RingFinishEnum.RoseGold}`,
  `${POP_PATHNAME}/${RingStyleEnum.OuraRing4}/${RingFinishEnum.Silver}`,
  `${POP_PATHNAME}/${RingStyleEnum.OuraRing4}/${RingFinishEnum.Stealth}`,
];

const useEyebrowProps = (): EyebrowProps => {
  const { displayMessage } = useDiscountMessage({
    isAccessory: false,
    isOuraRing4Wizard: true,
  });
  const showHardwarePDP = usePDPExperiment();

  const config = useDynamicConfig('eyebrow');

  const eyebrowConfig = config.value as EyebrowConfig;

  const activeEyebrowConfig = Object.keys(config.value).length > 0;

  const className =
    eyebrowConfig.className ?? 'bg-sandstone-200 text-sandstone-500';

  // Display the hidden afilliate eyebrow by default
  const DEFAULT = {
    displayMessage: AffiliateMessage,
    className: 'hidden',
    id: 'partner_eyebrow',
  };
  const pathnameWithoutLocale = usePathnameWithoutLocale();

  const isHardwarePDPRoute =
    showHardwarePDP && HARDWARE_PDP_ROUTES.includes(pathnameWithoutLocale);

  // organize by pathname and statsig config
  const routes = eyebrowConfig.routes;

  const activeConfigPath = routes?.length
    ? routes.includes(pathnameWithoutLocale)
    : pathnameWithoutLocale === '/';

  if (displayMessage && isHardwarePDPRoute) {
    return {
      className: 'bg-black',
      displayMessage: () => (
        <TypographyV3
          className="flex-wrap items-center justify-center gap-x-1 text-center"
          color="light"
          data-cy="discount-message-text"
          style={{ hyphens: 'none' }}
          variant="body-small"
          weight="bold"
        >
          {displayMessage}
        </TypographyV3>
      ),
    };
  }

  if (activeEyebrowConfig && activeConfigPath) {
    return {
      //Default class name styles for the eyebrow if no classname in config is provided
      className: className,
      displayMessage: () => (
        <GenericEyebrow {...(config.value as unknown as GenericEyebrowProps)} />
      ),
    };
  }

  // Display the hidden affiliate eyebrow by default
  return DEFAULT;
};

export default useEyebrowProps;
